import { useGetApplicableCouponsQuery, useGetBaggageInfoQuery, useGetMiniRuleInfoQuery } from '@/src/api/rtkQuery/srpFlightDetailsApi'
import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import { Fare, FlightCard, JsonsData, Sectors } from '@/src/types/flight'
import { QueryInput } from '@/src/types/searchQuery'
import { getTotalPriceForNewIntlRTT } from '@/src/utils/srp/RT/get-price'
import { getSplrtJourneyContextForRT, isRegularFareFamilyPlan } from '@/src/utils/srp/RT/getSplrtJourneyContext'
import { getSearchQueryObj } from '@/src/utils/srp/searchUtils'
import { getApplicableCouponsRequest } from '@/src/utils/srp/searchUtils/getApplicableCouponsRequest'
import { getSRPMiniRuleRequest } from '@/src/utils/srp/searchUtils/getMiniRuleRequest'
import { getSRPBaggageRequest } from '@/src/utils/srp/searchUtils/getSRPBaggageRequest'
import { skipToken } from '@reduxjs/toolkit/query'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import useFormatDate from '../../date/useFormatDate'
import { useGetSearchParamsObj } from '../searchParams'
import useSearchResults from '../useSearchResults'

/**
 * When fare-family plan is selected, we keep the original card fare as base (bc/ncib etc.)
 * and only override the keys that fare-family API actually controls.
 *
 * This keeps the payload shape identical for baggage + mini-rule APIs, while switching
 * to plan-specific fareKey / fareBasis (comboFbc) / basePrice / solutionId.
 */
function buildFareForFareFamilyPlan (cardFare: Fare, plan: Plan): Fare {
    return {
        ...cardFare,
        fk: plan.fareKey,
        fb: plan.comboFbc,
        bp: plan.basePrice,
        solutionId: plan.solutionId,
        bc: plan.bookingClass
    }
}

export const useFlightDetails = ({
    flights,
    selectedFareFamilyPlans,
    jsons,
    sectors,
    onSelect,
    sid,
    showBaggage = true,
    isRtSolutionPair
}: {
    flights: FlightCard[]
    selectedFareFamilyPlans?: (Plan | null)[]
    jsons: JsonsData
    sectors: Sectors
    onSelect: () => void
    sid: string
    showBaggage?: boolean
    isRtSolutionPair?: boolean
}) => {
    const { formatDate } = useFormatDate()
    const { urlQueryObj } = useGetSearchParamsObj()
    const t = useTranslations('common')
    const searchQueryObj = getSearchQueryObj(urlQueryObj as QueryInput)
    const { isPerPaxPricingEnabled, isMulticity } = useSearchResults()
    const [retryCount, setRetryCount] = useState(0)
    const MAX_RETRIES = 2

    /**
     * RT SPLRT context is shared between baggage + mini-rule request builders.
     * It uses the same SPLRT branching rules as `buildItineraryPayload`.
     */
    const rtSplrtContext = useMemo(() => {
        if (!(flights.length === 2 && isRtSolutionPair)) return null
        return getSplrtJourneyContextForRT({
            onwardCard: flights[0],
            returnCard: flights[1],
            onwardPlan: selectedFareFamilyPlans?.[0] ?? null,
            returnPlan: selectedFareFamilyPlans?.[1] ?? null
        })
    }, [flights, isRtSolutionPair, selectedFareFamilyPlans])

    // Baggage Request
    const baggageRequest = useMemo(() => {
        if (flights.length === 2 && isRtSolutionPair) {
            const [onwardDetails, returnDetails] = flights
            const onwardPlan = selectedFareFamilyPlans?.[0] ?? null
            const returnPlan = selectedFareFamilyPlans?.[1] ?? null
            const isSplrtJourney = !!rtSplrtContext?.isSplrtJourney
            const onwardSFare = rtSplrtContext?.onwardSFare
            const returnSFare = rtSplrtContext?.returnSFare

            // If fare-family plan is selected (non-regular), override fare keys for that leg only.
            const onwardFareForFareFamily =
                onwardPlan && !isRegularFareFamilyPlan(onwardPlan) && onwardDetails?.priceBreakup?.fare
                    ? buildFareForFareFamilyPlan(onwardDetails.priceBreakup.fare as Fare, onwardPlan)
                    : undefined
            const returnFareForFareFamily =
                returnPlan && !isRegularFareFamilyPlan(returnPlan) && returnDetails?.priceBreakup?.fare
                    ? buildFareForFareFamilyPlan(returnDetails.priceBreakup.fare as Fare, returnPlan)
                    : undefined

            const onwardBaggageRequest = getSRPBaggageRequest(
                onwardDetails,
                isMulticity,
                searchQueryObj,
                sectors,
                formatDate,
                jsons,
                // Priority:
                // - SPLRT fare (when allowed + valid)
                // - Fare-family selected fare (when non-regular)
                // - Default card fare (inside getSRPBaggageRequest)
                isSplrtJourney ? onwardSFare : onwardFareForFareFamily
            )
            const returnBaggageRequest = getSRPBaggageRequest(
                returnDetails,
                isMulticity,
                searchQueryObj,
                sectors,
                formatDate,
                jsons,
                isSplrtJourney ? returnSFare : returnFareForFareFamily
            )
            onwardBaggageRequest.sol.push(returnBaggageRequest.sol[0])
            return onwardBaggageRequest
        } else {
            return getSRPBaggageRequest(flights[0], isMulticity, searchQueryObj, sectors, formatDate, jsons)
        }
    }, [flights, isRtSolutionPair, selectedFareFamilyPlans, searchQueryObj, sectors, formatDate, jsons, isMulticity])

    // Baggage Data and state
    const {
        data: baggageData,
        error: baggageRTKError,
        isLoading: baggageLoading,
        refetch: refetchBaggageInfo
    } = useGetBaggageInfoQuery(baggageRequest, {
        skip: !showBaggage || !baggageRequest
    })

    // Mini Rule Request
    const miniRuleRequest = useMemo(() => {
        if (flights.length === 2 && isRtSolutionPair) {
            const [onwardDetails, returnDetails] = flights
            const onwardPlan = selectedFareFamilyPlans?.[0] ?? null
            const returnPlan = selectedFareFamilyPlans?.[1] ?? null
            const isSplrtJourney = !!rtSplrtContext?.isSplrtJourney
            const onwardSFare = rtSplrtContext?.onwardSFare
            const returnSFare = rtSplrtContext?.returnSFare
            const onwardSFareSolutionId = rtSplrtContext?.onwardSolutionId
            const returnSFareSolutionId = rtSplrtContext?.returnSolutionId

            // Fare-family fallback (only if non-regular selected):
            const onwardFareForFareFamily =
                onwardPlan && !isRegularFareFamilyPlan(onwardPlan) && onwardDetails?.priceBreakup?.fare
                    ? buildFareForFareFamilyPlan(onwardDetails.priceBreakup.fare as Fare, onwardPlan)
                    : undefined
            const returnFareForFareFamily =
                returnPlan && !isRegularFareFamilyPlan(returnPlan) && returnDetails?.priceBreakup?.fare
                    ? buildFareForFareFamilyPlan(returnDetails.priceBreakup.fare as Fare, returnPlan)
                    : undefined

            const onwardMiniRuleRequest = getSRPMiniRuleRequest(
                onwardDetails,
                isMulticity,
                searchQueryObj,
                sectors,
                formatDate,
                sid,
                jsons,
                // Priority:
                // - SPLRT solutionId (when allowed + valid)
                // - Fare-family selected solutionId (when non-regular)
                // - Default card solutionId
                isSplrtJourney
                    ? onwardSFareSolutionId
                    : onwardFareForFareFamily?.solutionId || onwardDetails.priceBreakup.fare.solutionId!,
                isSplrtJourney ? onwardSFare : onwardFareForFareFamily
            )

            const returnMiniRuleRequest = getSRPMiniRuleRequest(
                returnDetails,
                isMulticity,
                searchQueryObj,
                sectors,
                formatDate,
                sid,
                jsons,
                isSplrtJourney
                    ? returnSFareSolutionId
                    : returnFareForFareFamily?.solutionId || returnDetails.priceBreakup.fare.solutionId!,
                isSplrtJourney ? returnSFare : returnFareForFareFamily
            )

            return { payload: [onwardMiniRuleRequest, returnMiniRuleRequest], isSplrt: !!isSplrtJourney }
        }
        const oneWayMiniRuleRequest = getSRPMiniRuleRequest(
            flights[0],
            isMulticity,
            searchQueryObj,
            sectors,
            formatDate,
            sid,
            jsons,
            flights?.[0]?.priceBreakup.fare.solutionId!
        )
        return { payload: [oneWayMiniRuleRequest], isSplrt: false }
    }, [flights, isRtSolutionPair, selectedFareFamilyPlans, searchQueryObj, sectors, formatDate, sid, jsons])

    // Mini Rule Data and state
    const {
        data: minirule,
        isLoading: miniRuleLoading,
        error: miniRuleRTKError,
        refetch: refetchMiniRulesInfo
    } = useGetMiniRuleInfoQuery(miniRuleRequest)

    const prices = useMemo(() => {
        if (isRtSolutionPair) {
            if (flights.length === 2) {
                const { totalPriceTwoLegs, totalStrikeOffPriceTwoLegs } = getTotalPriceForNewIntlRTT(
                    flights,
                    selectedFareFamilyPlans
                )
                return {
                    price: totalPriceTwoLegs,
                    strikeOffPrice: totalStrikeOffPriceTwoLegs
                }
            } else {
                return {
                    price: flights[0].priceBreakup.multiAirlinePrice,
                    strikeOffPrice: flights[0].priceBreakup.multiAirlinePriceWoDiscount
                }
            }
        }
        //one way
        return {
            price: isPerPaxPricingEnabled ? flights[0].priceBreakup?.totalAdultFare : flights[0].priceBreakup.pr,
            strikeOffPrice: isPerPaxPricingEnabled
                ? flights[0].priceBreakup?.fare?.per_adult_pricing?.strikeOffDetails?.strikedOffPrice
                : flights[0].priceBreakup?.fare?.strike_off_details?.strikedOffPrice
        }
    }, [flights, isRtSolutionPair, isPerPaxPricingEnabled])

    const couponsFareCategory = useMemo(() => {
        return rtSplrtContext?.isSplrtJourney ? 'SPECIAL_RT' : 'RETAIL'
    }, [rtSplrtContext])

    const couponsTotalFare = useMemo(() => {
        if (isRtSolutionPair) {
            if (flights.length === 2) {
                const { totalPriceTwoLegs, totalStrikeOffPriceTwoLegs } = getTotalPriceForNewIntlRTT(
                    flights,
                    selectedFareFamilyPlans
                )
                return totalStrikeOffPriceTwoLegs || totalPriceTwoLegs
            }
            return (
                flights[0]?.priceBreakup?.multiAirlinePriceWoDiscount ||
                flights[0]?.priceBreakup?.multiAirlinePrice ||
                flights[0]?.priceBreakup?.pr ||
                0
            )
        }
        return isPerPaxPricingEnabled
            ? flights[0]?.priceBreakup?.fare?.per_adult_pricing?.strikeOffDetails?.strikedOffPrice ||
                  flights[0]?.priceBreakup?.totalAdultFare ||
                  0
            : flights[0]?.priceBreakup?.fare?.strike_off_details?.strikedOffPrice || flights[0]?.priceBreakup?.pr || 0
    }, [flights, isRtSolutionPair, isPerPaxPricingEnabled, selectedFareFamilyPlans])

    const couponsBaseFare = useMemo(() => {
        if (isRtSolutionPair) {
            if (flights.length === 2) {
                const onwardDetails = flights[0]
                const returnDetails = flights[1]
                const onwardPlan = selectedFareFamilyPlans?.[0] ?? null
                const returnPlan = selectedFareFamilyPlans?.[1] ?? null
                const isSplrtJourney = !!rtSplrtContext?.isSplrtJourney
                const onwardSFare = rtSplrtContext?.onwardSFare
                const returnSFare = rtSplrtContext?.returnSFare

                const onwardBaseFare = isSplrtJourney
                    ? onwardSFare?.bp || 0
                    : onwardPlan && !isRegularFareFamilyPlan(onwardPlan)
                        ? onwardPlan.basePrice || 0
                        : onwardDetails?.priceBreakup?.fare?.bp || 0

                const returnBaseFare = isSplrtJourney
                    ? returnSFare?.bp || 0
                    : returnPlan && !isRegularFareFamilyPlan(returnPlan)
                        ? returnPlan.basePrice || 0
                        : returnDetails?.priceBreakup?.fare?.bp || 0

                return onwardBaseFare + returnBaseFare
            }

            return flights[0]?.priceBreakup?.fare?.bp || 0
        }

        const onwardPlan = selectedFareFamilyPlans?.[0] ?? null
        if (onwardPlan) {
            return onwardPlan.basePrice || 0
        }

        return flights[0]?.priceBreakup?.fare?.bp || 0
    }, [flights, isRtSolutionPair, selectedFareFamilyPlans, rtSplrtContext])

    const couponsRequest = useMemo(() => {
        return getApplicableCouponsRequest({
            flights,
            sectors,
            jsons,
            sid,
            urlQueryObj: searchQueryObj,
            totalFare: couponsTotalFare,
            baseFare: couponsBaseFare,
            fareCategory: couponsFareCategory
        })
    }, [flights, sectors, jsons, sid, searchQueryObj, couponsTotalFare, couponsBaseFare, couponsFareCategory])

    const {
        data: applicableCouponsData,
        isLoading: applicableCouponsLoading,
        error: applicableCouponsRTKError
    } = useGetApplicableCouponsQuery(isMulticity ? skipToken : (couponsRequest ?? skipToken))

    const footerButtonCTA = useMemo(() => {
        if (isRtSolutionPair) {
            if (flights.length === 2) {
                return t('bookNow')
            }
            return t('Select')
        }
        return t('bookNow')
    }, [isRtSolutionPair])

    // Handle retry logic
    const handleRetryBaggage = () => {
        if (retryCount < MAX_RETRIES) {
            setRetryCount(prev => prev + 1)
            refetchBaggageInfo()
        }
    }

    return {
        baggageData,
        baggageRTKError,
        baggageLoading,
        refetchBaggageInfo,
        minirule,
        miniRuleRTKError,
        miniRuleLoading,
        refetchMiniRulesInfo,
        prices,
        footerButtonCTA,
        retryCount,
        handleRetryBaggage,
        maxRetries: MAX_RETRIES,
        applicableCouponsData,
        applicableCouponsLoading,
        applicableCouponsRTKError
    }
}
